import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Button, Box, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AssignmentInd as AssignmentIndIcon, PersonRemove as PersonRemoveIcon } from "@mui/icons-material";
import { getActiveUsers } from "../../redux/actions/GetActiveUsersAction";
import { GetActiveRolesByCountry } from "../../redux/actions/GetActiveRolesByCountryAction";
import GrantUserRolesModal from "components/GrantUserRolesModal";
import RemoveUserRolesModal from "components/RemoveUserRolesModal";

const UserRolesTable = () => {
  const dispatch = useDispatch();
  const usersResponse = useSelector((state) => state.activeUsers.users);
  const usersStatus = useSelector((state) => state.activeUsers.status);
  const rolesResponse = useSelector((state) => state.activeRoles.roles);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [openGrantModal, setOpenGrantModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (usersResponse) {
      const listUsers = usersResponse.map((element) => {
        const rols = (element.rols || []).map((role) => ({
          idrole: role.idrole ?? role.idRole,
          roleName: role.roleName,
        }));

        return {
          id: element.iduser ?? element.idUser,
          iduser: element.iduser ?? element.idUser,
          userName: element.userName,
          rols,
        };
      });

      setUsers(listUsers);
    }
  }, [usersResponse]);

  useEffect(() => {
    if (rolesResponse?.length > 0) {
      const listRoles = rolesResponse.map((role) => ({
        idRole: role.idrole ?? role.idRole,
        roleName: role.roleName,
      }));
      setRoles(listRoles);
    }
  }, [rolesResponse]);

  useEffect(() => {
    dispatch(getActiveUsers());
    dispatch(GetActiveRolesByCountry());
  }, [dispatch]);

  const handleGrantOpen = (row) => {
    setSelectedUser(row);
    setOpenGrantModal(true);
  };

  const handleRemoveOpen = (row) => {
    setSelectedUser(row);
    setOpenRemoveModal(true);
  };

  const columns = [
    {
      field: "iduser",
      headerName: "ID",
      width: 80,
    },
    {
      field: "userName",
      headerName: "User Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "rols",
      headerName: "Assigned Roles",
      flex: 1.5,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 1 }}>
          {(params.value || []).map((role) => (
            <Chip
              key={role.idrole}
              label={role.roleName}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", py: 1 }}>
          <Button
            variant="contained"
            startIcon={<AssignmentIndIcon />}
            sx={{
              color: "#ffffff",
              backgroundColor: "#4b4bfa",
              "&:hover": { backgroundColor: "#3e3ed9" },
            }}
            onClick={() => handleGrantOpen(params.row)}
          >
            Grant
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PersonRemoveIcon />}
            onClick={() => handleRemoveOpen(params.row)}
            disabled={(params.row.rols || []).length === 0}
          >
            Remove
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <div style={{ height: "100%", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <h1 style={{ marginBottom: "0.5%" }}>User Roles</h1>
            </Box>
            <DataGrid
              rows={users}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              loading={usersStatus === "loading"}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiCircularProgress-root": { color: "#4b4bfa" },
                mt: 2,
                "& .MuiDataGrid-cell": { py: 1, alignItems: "flex-start" },
              }}
            />
          </div>
        </Card>
      </Grid>

      <GrantUserRolesModal
        roles={roles}
        open={openGrantModal}
        onClose={() => {
          setOpenGrantModal(false);
          setSelectedUser(null);
        }}
        userData={selectedUser}
      />
      <RemoveUserRolesModal
        open={openRemoveModal}
        onClose={() => {
          setOpenRemoveModal(false);
          setSelectedUser(null);
        }}
        userData={selectedUser}
      />
    </Grid>
  );
};

export default UserRolesTable;
